// @flow

// CloseupLink is an abstraction over NavigateLink, taking the user to the Pin
// Closeup and providing a number of props that would otherwise be repeated.

import React, { type Node } from 'react';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import { buildPinDeeplinks } from 'app/common/lib/deeplinkRoutes';
import { getCommerceAuxData } from 'app/common/lib/CommerceUtil';
import { startTiming } from 'app/common/lib/TraceLogger/logTraceEvent';
import { track } from 'app/common/lib/react/analytics';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { useTrackingParameters } from 'app/common/react/components/PinRep/TrackingParametersContext';

type Props = {|
  children: Node,
  collageChipId?: string,
  pin: Pin,
  additionalStyles?: Object,

  // Tracking Params (override TrackingParametersContext)
  componentType?: $Values<PCONST.thrift.ComponentType>,
  contextLogData?: Object,
  elementType?: $Values<PCONST.thrift.ElementType>,
  viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
  viewType?: $Values<PCONST.thrift.ViewType>,
|};

export default function CloseupLink({
  additionalStyles = {},
  children,
  collageChipId,
  componentType,
  contextLogData,
  elementType,
  pin,
  viewParameter,
  viewType,
}: Props) {
  const defaultTrackingParameters = useTrackingParameters();

  const { carousel_data, id: pinId } = pin;

  let auxData;
  let url = `/pin/${collageChipId || pinId}/`;

  if (carousel_data) {
    // Build the carousel slot's link. If the pin is a carousel pin, the link
    // should take the user to the current slot.
    const { carousel_slots = [], id, index } = carousel_data;

    url = `/pin/${pinId}/`;
    auxData = {
      carousel_slot_id: carousel_slots[index] && carousel_slots[index].id,
      carousel_data_id: id,
      carousel_slot_index: index,
    };
  }

  auxData = { ...(auxData || {}), ...(getCommerceAuxData(pin, contextLogData) || {}) };

  const handleClick = () => {
    startTiming(PCONST.thrift.TracingClientTypes.PWTAction.PIN_CLOSEUP_DETAILS);

    track(PCONST.thrift.EventType.CLICK, {
      ...contextLogData,
      component: componentType || defaultTrackingParameters.componentType,
      element: elementType,
      objectId: pinId,
      view: viewType || defaultTrackingParameters.viewType,
      viewParameter: viewParameter || defaultTrackingParameters.viewParameter,
      ...(getCommerceAuxData(pin, contextLogData) || {}),
    });
  };

  return (
    // Don't wrap this markup, as it can break the passed-in additional styles
    <NavigateLink
      auxData={auxData}
      deeplinkUrls={buildPinDeeplinks(pinId)}
      forceRefresh={PCONST.ForceRefreshType.PAGE}
      onClick={handleClick}
      pinId={pinId}
      style={{
        cursor: 'zoom-in',
        display: 'block',
        ...additionalStyles,
      }}
      to={url}
    >
      {children}
    </NavigateLink>
  );
}
